import { inject, Injectable } from '@angular/core';
import { SESSION_STORAGE } from '@shopware-operations/angular-utils';

/**
 * OAuth auto-accept context stored after registration/recovery with OAuth.
 * 
 * @remarks
 * Used to identify if a specific OAuth login request should be auto-accepted
 * based on recent registration + verification or recovery + password change.
 * 
 * Storage is session-scoped (cleared on tab close) and single-use (deleted after match).
 */
export interface OAuthAutoAcceptContext {
  /** Kratos session ID created during registration/recovery */
  sessionId: string;
  
  /** User identity ID */
  identityId: string;
  
  /** OAuth client ID */
  clientId: string;
  
  /** Full OAuth request URL (includes state, redirect_uri, scope, etc.) */
  requestUrl: string;
  
  /** Flow type that created this context */
  flow: 'registration' | 'recovery';
  
  /** Timestamp when context was created */
  timestamp: number;
  
  /** For registration flow: marks if email verification completed */
  verificationCompleted?: boolean;
}

/**
 * Service for managing OAuth auto-accept context in sessionStorage.
 * 
 * @remarks
 * **Security Design:**
 * - Uses exact `request_url` match (includes OAuth `state` parameter for CSRF protection)
 * - Single-use: Context deleted after successful auto-accept
 * - Session-scoped: Cleared on tab close (uses sessionStorage)
 * - No time-based checks needed (exact URL match is sufficient)
 * 
 * **Storage Key Format:**
 * `oauth-auto-accept:{sessionId}:{requestUrlHash}`
 * 
 * **Use Cases:**
 * 1. **Registration + Verification + OAuth**: Auto-accept after email verification
 * 2. **Recovery + Password Change + OAuth**: Auto-accept after password reset
 * 
 * @example
 * ```typescript
 * // After registration with OAuth
 * const context: OAuthAutoAcceptContext = {
 *   sessionId: '6da3f90a-910e-42c1-8ba4-e3cce6d9dee5',
 *   identityId: '9aebf39f-e81f-452f-ad3d-95cbb364e26a',
 *   clientId: '47378176-3230-4784-9a41-da29d72bb382',
 *   requestUrl: 'http://localhost:5100/oauth2/auth?client_id=...&state=...',
 *   flow: 'registration',
 *   timestamp: Date.now(),
 *   verificationCompleted: false
 * };
 * service.storeContext(context);
 * 
 * // After verification
 * service.markVerificationComplete(sessionId);
 * 
 * // On login guard
 * const key = service.generateKey(sessionId, requestUrl);
 * const context = service.getContext(key);
 * if (context && context.verificationCompleted) {
 *   // Auto-accept OAuth login
 *   service.clearContext(key);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class OAuthAutoAcceptStorageService {
  
  readonly #storage = inject(SESSION_STORAGE);
  readonly #storagePrefix = 'oauth-auto-accept';
  
  /**
   * Stores OAuth auto-accept context.
   * 
   * @param context - OAuth context to store
   */
  storeContext(context: OAuthAutoAcceptContext): void {
    const key = this.generateKey(context.sessionId, context.requestUrl);
    
    try {
      this.#storage.setItem(key, JSON.stringify(context));
    } catch {
      // Storage error (quota exceeded, disabled) - fail silently
      // User will see normal login UI (acceptable fallback)
    }
  }
  
  /**
   * Retrieves OAuth auto-accept context by storage key.
   * 
   * @param key - Storage key generated by generateKey()
   * @returns Context if found, undefined otherwise
   */
  getContext(key: string): OAuthAutoAcceptContext | undefined {
    try {
      const data = this.#storage.getItem(key);
      if (!data) {
        return undefined;
      }
      
      return JSON.parse(data) as OAuthAutoAcceptContext;
    } catch {
      // Parse error or storage disabled - fail silently
      return undefined;
    }
  }
  
  /**
   * Marks verification as completed for a session's OAuth context.
   * 
   * @remarks
   * Scans sessionStorage for contexts matching the session ID and updates them.
   * Used after email verification to enable auto-accept for registration flow.
   * 
   * @param sessionId - Kratos session ID
   */
  markVerificationComplete(sessionId: string): void {
    try {
      const length = this.#storage.length();
      
      // Scan for keys matching this session
      for (let i = 0; i < length; i++) {
        const key = this.#storage.key(i);
        
        if (key?.startsWith(`${this.#storagePrefix}:${sessionId}:`)) {
          const data = this.#storage.getItem(key);
          if (data) {
            const context: OAuthAutoAcceptContext = JSON.parse(data);
            context.verificationCompleted = true;
            this.#storage.setItem(key, JSON.stringify(context));
          }
        }
      }
    } catch {
      // Storage error - fail silently
    }
  }
  
  /**
   * Removes OAuth auto-accept context (single-use after successful match).
   * 
   * @param key - Storage key generated by generateKey()
   */
  clearContext(key: string): void {
    try {
      this.#storage.removeItem(key);
    } catch {
      // Storage error - fail silently
    }
  }
  
  /**
   * Clears all OAuth auto-accept contexts.
   * 
   * @remarks
   * Used on logout to ensure no stale contexts remain.
   */
  clearAll(): void {
    try {
      const length = this.#storage.length();
      const keysToRemove: string[] = [];
      
      // Collect keys to remove (can't modify during iteration)
      for (let i = 0; i < length; i++) {
        const key = this.#storage.key(i);
        if (key?.startsWith(this.#storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      
      // Remove collected keys
      keysToRemove.forEach(key => this.#storage.removeItem(key));
    } catch {
      // Storage error - fail silently
    }
  }
  
  /**
   * Generates unique storage key from session ID and request URL.
   * 
   * @remarks
   * Format: `oauth-auto-accept:{sessionId}:{requestUrlHash}`
   * 
   * Uses simple hash of requestUrl for key length management while maintaining uniqueness.
   * Hash collisions are acceptable (just means we retrieve and validate the full URL).
   * 
   * **Public API:**
   * Exposed so callers can generate the key once and reuse for both get and clear operations.
   * 
   * @param sessionId - Kratos session ID
   * @param requestUrl - Full OAuth request URL
   * @returns Unique storage key
   */
  generateKey(sessionId: string, requestUrl: string): string {
    const hash = this.#simpleHash(requestUrl);
    return `${this.#storagePrefix}:${sessionId}:${hash}`;
  }
  
  /**
   * Simple hash function for request URL.
   * 
   * @remarks
   * Not cryptographic - just for key length management.
   * Collisions are acceptable as we validate the full requestUrl after retrieval.
   */
  #simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }
}

